import { useEffect, useState } from 'react';

import { useFlag, useFlagsStatus, useVariant } from '@unleash/proxy-client-react';
import { IVariant } from 'unleash-proxy-client';

import { getUnleashClient } from './client';
import { flags, FlagNames } from './featureFlagConfig';

function useXWithFallback(name: FlagNames) {
  const { flagsReady, flagsError } = useFlagsStatus();

  return !flagsReady || flagsError ? flags[name] : undefined;
}

function useFlagWithFallback(name: FlagNames): [boolean, { fallback: boolean }] {
  const fallback = useXWithFallback(name);
  const enabled = useFlag(name);

  return [enabled ?? fallback?.enabled, { fallback: !!fallback }];
}

function useVariantWithFallback(name: FlagNames): [Partial<IVariant> | undefined, { fallback: boolean }] {
  const fallback = useXWithFallback(name);
  const variant = useVariant(name);

  return [fallback ? fallback.variant : variant, { fallback: !!fallback }];
}

function useMultipleFlags(names: FlagNames[]) {
  const [selectedFlag, setSelectedFlag] = useState<FlagNames>();
  const [selectedVariant, setSelectedVariant] = useState<Partial<IVariant> | undefined>({ enabled: false, name: 'disabled' });
  const { flagsReady } = useFlagsStatus();

  useEffect(() => {
    if (flagsReady) {
      const unleash = getUnleashClient();
      const name = names.find((flagName) => unleash.isEnabled(flagName));

      if (name) {
        const variant = unleash.getVariant(name);

        setSelectedVariant(variant);
      }

      setSelectedFlag(name);
    }
  }, [flagsReady, names]);

  return { flagName: selectedFlag ?? names[0], variant: selectedVariant };
}

export { useFlagWithFallback, useVariantWithFallback, useMultipleFlags };
